import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  AccessibilityInfo,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import Ionicons from "@expo/vector-icons/Ionicons";
import Slider from "@react-native-community/slider";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import Animated, {
  Easing,
  SensorType,
  cancelAnimation,
  interpolate,
  useAnimatedReaction,
  useAnimatedSensor,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
  type SharedValue,
} from "react-native-reanimated";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";
import { KeyboardScrollView } from "@/components/ui/keyboard-view";
import { useTranslation } from "react-i18next";

import { moodJarApi } from "@/api";
import { notify } from "@/lib/dialog";
import {
  HOLD_THRESHOLD_MS,
  JAR_TIMING,
  SHAKE_COOLDOWN_MS,
  SHAKE_TRIGGER,
  TILT_SMOOTHING,
  accumulateShake,
  composeTokenOffset,
  gravityToTilt,
  smoothToward,
  type TokenMotionConstants,
} from "@/lib/mood-jar-motion";
import { type TranslationKey } from "@/i18n";
import { useAppTheme, type AppColors } from "@/theme/app-theme";
import type {
  MoodCatalog,
  MoodCatalogItem,
  MoodContextCatalogItem,
  MoodJarEntry,
  MoodJarSummary,
} from "@/types";

const MOOD_OTHER_MAX = 20;
const CONTEXT_OTHER_MAX = 25;
const VISIBLE_TOKEN_LIMIT = 35;
const JAR_BODY_WIDTH = 236;
const JAR_BODY_HEIGHT = 218;
const JAR_TOKEN_INSET = 8;
const INTENSITY_KEYS = [
  "profile.moodJar.intensity.aLittle",
  "profile.moodJar.intensity.aBit",
  "profile.moodJar.intensity.medium",
  "profile.moodJar.intensity.strong",
  "profile.moodJar.intensity.aLot",
] as const;

type MoodJarVisualToken = {
  id: string;
  emoji: string;
  intensity: number;
  moodId: string;
  color: string;
  accent: string;
  rotation: number;
};

type TokenLayout = {
  size: number;
  left: number;
  bottom: number;
  opacity: number;
  zIndex: number;
  rotate: string;
};

/**
 * Mood Jar capture. Dropping a mood NEVER generates anything — it just records
 * controlled mood data. The accumulated jar becomes a Monthly Vibe Card when
 * the month ends.
 */
export default function MoodJarScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ returnTo?: string }>();
  const { t } = useTranslation();
  const { colors, statusBarStyle } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [catalog, setCatalog] = useState<MoodCatalog>({ moods: [], contexts: [] });
  const [summary, setSummary] = useState<MoodJarSummary | null>(null);
  const [recent, setRecent] = useState<MoodJarEntry[]>([]);
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [moodOther, setMoodOther] = useState("");
  const [selectedContext, setSelectedContext] = useState<string | null>(null);
  const [contextOther, setContextOther] = useState("");
  const [intensity, setIntensity] = useState(3);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [jarPulse, setJarPulse] = useState(0);
  const [moodPickerExpanded, setMoodPickerExpanded] = useState(false);

  const moodOptions = useMemo(() => catalog.moods, [catalog.moods]);
  const contextOptions = useMemo(() => catalog.contexts, [catalog.contexts]);
  const currentPeriodKey = summary?.periodKey ?? currentMonthPeriodKey();
  const currentMonthEntries = useMemo(
    () =>
      recent
        .filter((entry) => entry.createdAt.slice(0, 7) === currentPeriodKey)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [currentPeriodKey, recent],
  );
  const visualTokens = useMemo(
    () => buildVisualTokens(currentMonthEntries, moodOptions),
    [currentMonthEntries, moodOptions],
  );
  const jarEntryCount = summary?.entryCount ?? currentMonthEntries.length;
  const selectedIntensityLabel = t(INTENSITY_KEYS[intensity - 1]);
  const trimmedMoodOther = moodOther.trim().slice(0, MOOD_OTHER_MAX);
  const canAdd = Boolean(
    selectedMood &&
      !saving &&
      (selectedMood !== "other" || trimmedMoodOther.length > 0),
  );

  const load = useCallback(() => {
    moodJarApi.catalog().then(setCatalog).catch(() => {});
    moodJarApi.summary().then(setSummary).catch(() => {});
    moodJarApi.recent().then(setRecent).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const close = useCallback(() => {
    if (params.returnTo === "dashboard") {
      router.replace("/dashboard");
      return;
    }

    router.replace("/profile");
  }, [params.returnTo, router]);

  const moodLabel = useCallback(
    (id: string) => t(`profile.moodJar.moods.${id}` as TranslationKey),
    [t],
  );

  const handleMoodSelect = useCallback((id: string) => {
    setSelectedMood((current) => {
      const next = current === id ? null : id;
      if (next !== "other") setMoodOther("");
      return next;
    });
  }, []);

  async function add() {
    if (!selectedMood || !canAdd) return;
    const trimmedContextOther = contextOther.trim().slice(0, CONTEXT_OTHER_MAX);
    setSaving(true);
    try {
      await moodJarApi.add({
        moodId: selectedMood,
        moodOther: selectedMood === "other" ? trimmedMoodOther : null,
        intensity,
        contextId: selectedContext,
        contextOther: selectedContext === "other" ? trimmedContextOther : null,
      });
      const wasGentle = selectedMood === "grieving";
      setSelectedMood(null);
      setMoodOther("");
      setSelectedContext(null);
      setContextOther("");
      setIntensity(3);
      notify(t(wasGentle ? "profile.moodJar.successGentle" : "profile.moodJar.success"));
      setJarPulse((current) => current + 1);
      moodJarApi.summary().then(setSummary).catch(() => {});
      moodJarApi.recent().then(setRecent).catch(() => {});
    } catch {
      notify(t("profile.moodJar.error"));
    } finally {
      setSaving(false);
    }
  }

  async function devSeed() {
    if (seeding) return;
    setSeeding(true);
    try {
      const summary = await moodJarApi.devSeed();
      notify(`Seeded ${summary.entryCount} moods into ${summary.periodKey}.`);
      setSummary(summary);
      moodJarApi.recent().then(setRecent).catch(() => {});
      router.push(`/vibe-card?period=${summary.periodKey}`);
    } catch {
      notify(t("profile.moodJar.error"));
    } finally {
      setSeeding(false);
    }
  }

  const contextLabel = useCallback(
    (id: string) => t(`profile.moodJar.contexts.${id}` as TranslationKey),
    [t],
  );

  const jarSummaryLabel =
    jarEntryCount === 0
      ? t("profile.moodJar.jar.emptySummary")
      : jarEntryCount === 1
        ? t("profile.moodJar.jar.singleSummary")
        : t("profile.moodJar.jar.countSummary", { count: jarEntryCount });

  return (
    <SafeAreaView style={styles.safe} edges={["top", "bottom"]}>
      <StatusBar style={statusBarStyle} />

      <View style={styles.header}>
        <Pressable
          onPress={close}
          accessibilityRole="button"
          accessibilityLabel={t("profile.monthlyVibeCard.editor.close")}
          hitSlop={10}
          style={styles.headerBtn}
        >
          <Ionicons name="close" size={24} color={colors.text} />
        </Pressable>
        <Text accessibilityRole="header" style={styles.headerTitle}>
          {t("profile.moodJar.title")}
        </Text>
        <View style={styles.headerBtn} />
      </View>

      <KeyboardScrollView
        contentContainerStyle={styles.body}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.form}>
          <MoodJarHero
            tokens={visualTokens}
            entryCount={jarEntryCount}
            summaryLabel={jarSummaryLabel}
            burstSeed={jarPulse}
            styles={styles}
            colors={colors}
          />

          <MoodOptionSection
            title={t("profile.moodJar.mood.heading")}
            options={moodOptions}
            selectedId={selectedMood}
            labelFor={moodLabel}
            onSelect={handleMoodSelect}
            styles={styles}
            colors={colors}
            optionKindKey="profile.moodJar.a11yMoodOption"
            otherValue={moodOther}
            onOtherChange={(value) => setMoodOther(value.slice(0, MOOD_OTHER_MAX))}
            otherPlaceholder={t("profile.moodJar.mood.otherPlaceholder")}
            otherCharacterCount={`${moodOther.length}/${MOOD_OTHER_MAX}`}
            showOtherInput={selectedMood === "other"}
            collapsible
            expanded={moodPickerExpanded}
            collapsedRows={2}
            toggleExpanded={() => setMoodPickerExpanded((expanded) => !expanded)}
            showMoreLabel={t("profile.moodJar.mood.showMore")}
            showLessLabel={t("profile.moodJar.mood.showLess")}
          />

          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text accessibilityRole="header" style={styles.sectionTitle}>
                {t("profile.moodJar.intensity.heading")}
              </Text>
              <Text style={styles.sectionBadge}>{selectedIntensityLabel}</Text>
            </View>
            <View style={styles.intensityRow}>
              <Text style={styles.intensityHint}>{t("profile.moodJar.intensity.aLittle")}</Text>
              <Slider
                value={intensity}
                minimumValue={1}
                maximumValue={5}
                step={1}
                onValueChange={(value) => setIntensity(Math.round(value))}
                minimumTrackTintColor={colors.primary}
                maximumTrackTintColor={colors.border}
                thumbTintColor={colors.primary}
                accessibilityLabel={t("profile.moodJar.intensity.heading")}
                accessibilityValue={{
                  min: 1,
                  max: 5,
                  now: intensity,
                  text: selectedIntensityLabel,
                }}
                style={styles.intensitySlider}
              />
              <Text style={styles.intensityHint}>{t("profile.moodJar.intensity.aLot")}</Text>
            </View>
          </View>

          <MoodContextSection
            title={t("profile.moodJar.context.heading")}
            badge={t("profile.moodJar.context.optional")}
            options={contextOptions}
            selectedId={selectedContext}
            labelFor={contextLabel}
            onSelect={(id) => setSelectedContext((current) => (current === id ? null : id))}
            contextOther={contextOther}
            onContextOtherChange={(value) => setContextOther(value.slice(0, CONTEXT_OTHER_MAX))}
            styles={styles}
            colors={colors}
            placeholder={t("profile.moodJar.context.otherPlaceholder")}
            characterCount={`${contextOther.length}/${CONTEXT_OTHER_MAX}`}
          />

          <Pressable
            onPress={add}
            disabled={!canAdd}
            accessibilityRole="button"
            accessibilityLabel={t("profile.moodJar.submit")}
            accessibilityState={{ disabled: !canAdd }}
            style={[styles.addBtn, !canAdd && styles.btnDisabled]}
          >
            {saving ? (
              <ActivityIndicator color="#fff" size="small" />
            ) : (
              <>
                <Text style={styles.addText}>{t("profile.moodJar.submit")}</Text>
                <Ionicons name="arrow-forward" size={20} color="#fff" />
              </>
            )}
          </Pressable>

          {__DEV__ ? (
            <Pressable
              onPress={devSeed}
              disabled={seeding}
              accessibilityRole="button"
              accessibilityLabel="Dev: seed last month"
              style={[styles.devBtn, seeding && styles.btnDisabled]}
            >
              {seeding ? (
                <ActivityIndicator color={colors.textSecondary} size="small" />
              ) : (
                <>
                  <Ionicons name="construct-outline" size={16} color={colors.textSecondary} />
                  <Text style={styles.devBtnText}>Dev: seed last month&apos;s jar</Text>
                </>
              )}
            </Pressable>
          ) : null}
        </View>
      </KeyboardScrollView>
    </SafeAreaView>
  );
}

/**
 * The sensors, isolated in a component that renders nothing.
 *
 * Mounted only while the jar should actually respond, because
 * `useAnimatedSensor` has an `unregister` and no matching `register` — once
 * stopped it cannot be restarted. Mounting and unmounting the hook's OWNER is
 * therefore the only way to turn the sensors off and on again, and this screen
 * is a tab that stays mounted in the background, so "off" has to be reachable
 * or the accelerometer runs for as long as the app is open.
 *
 * Everything here stays on the UI thread. The reactions write shared values and
 * start animations directly rather than hopping to JS through `runOnJS`: a
 * sensor firing at 60 Hz is exactly the traffic that should never cross the
 * bridge, and the storm is a shared-value animation that needs nothing from the
 * React side to begin.
 */
function JarSensors({
  tiltX,
  tiltY,
  storm,
  stormClock,
  onAvailability,
}: {
  tiltX: SharedValue<number>;
  tiltY: SharedValue<number>;
  storm: SharedValue<number>;
  stormClock: SharedValue<number>;
  onAvailability: (available: boolean) => void;
}) {
  // 60 ms rather than "auto". Tilt is a slow lean and a shake is a coarse
  // event; neither is improved by sampling faster, and the sensor is the one
  // thing on this screen that costs battery whether or not anybody is looking.
  const gravity = useAnimatedSensor(SensorType.GRAVITY, {
    interval: 60,
    adjustToInterfaceOrientation: true,
  });
  const accelerometer = useAnimatedSensor(SensorType.ACCELEROMETER, {
    interval: 60,
    adjustToInterfaceOrientation: true,
  });

  const energy = useSharedValue(0);
  const lastSample = useSharedValue({ x: 0, y: 0, z: 0 });
  const lastStormAt = useSharedValue(0);

  useAnimatedReaction(
    () => gravity.sensor.get(),
    (g) => {
      const tilt = gravityToTilt(g);
      // Low-passed, because a still phone still jitters by hundredths of a g
      // and thirty-five bubbles twitching in unison reads as a rendering fault.
      tiltX.set(smoothToward(tiltX.get(), tilt.x, TILT_SMOOTHING));
      tiltY.set(smoothToward(tiltY.get(), tilt.y, TILT_SMOOTHING));
    },
  );

  useAnimatedReaction(
    () => accelerometer.sensor.get(),
    (sample) => {
      const next = accumulateShake(energy.get(), lastSample.get(), sample);
      lastSample.set(next.previous);

      const now = Date.now();
      if (
        next.energy > SHAKE_TRIGGER &&
        now - lastStormAt.get() > SHAKE_COOLDOWN_MS
      ) {
        lastStormAt.set(now);
        // Spend the energy on the storm it just bought. Without this reset a
        // long shake keeps the accumulator above the trigger and fires again
        // the instant the cooldown lapses.
        energy.set(0);
        storm.set(withSequence(
          withTiming(1, { duration: JAR_TIMING.stormRise }),
          withTiming(0, { duration: JAR_TIMING.stormSettle }, (finished) => {
            // Stop the orbit when the storm has actually finished. Without
            // this the clock repeats forever on the UI thread, animating a
            // value every frame that `composeTokenOffset` stops reading the
            // moment `storm` reaches 0.
            if (finished) cancelAnimation(stormClock);
          }),
        ));
        stormClock.set(0);
        stormClock.set(withRepeat(
          withTiming(1, { duration: JAR_TIMING.stormOrbit, easing: Easing.linear }),
          -1,
          false,
        ));
      } else {
        energy.set(next.energy);
      }
    },
  );

  /**
   * Report upward, rather than having the parent guess from `Platform`.
   *
   * Availability is the sensor's own answer: a browser has none, and neither
   * does a simulator. The parent uses it for the MICROCOPY, so the jar never
   * advertises a tilt the device cannot feel — the Stay Visible row shipped
   * exactly that bug, offering a capability every layer beneath it knew was
   * impossible and only admitting it after the tap.
   */
  const available = gravity.isAvailable && accelerometer.isAvailable;
  useEffect(() => {
    onAvailability(available);
    return () => onAvailability(false);
  }, [available, onAvailability]);

  return null;
}

/**
 * One bubble.
 *
 * A component rather than a chunk of the parent's map callback, because
 * `useAnimatedStyle` is a hook and a hook cannot live inside a loop. Each
 * bubble therefore owns its own worklet, which is also what allows the crowd to
 * churn: thirty-five styles reading shared values on the UI thread, each with
 * its own phase, and no bridge traffic at all.
 */
function JarToken({
  token,
  layout,
  motion,
  constants,
  styles,
  stir,
  tiltX,
  tiltY,
  gather,
  scatter,
  storm,
  stormClock,
  intensity,
}: {
  token: MoodJarVisualToken;
  layout: TokenLayout;
  motion: ReturnType<typeof tokenBubbleMotion>;
  constants: TokenMotionConstants;
  styles: ReturnType<typeof createStyles>;
  stir: SharedValue<number>;
  tiltX: SharedValue<number>;
  tiltY: SharedValue<number>;
  gather: SharedValue<number>;
  scatter: SharedValue<number>;
  storm: SharedValue<number>;
  stormClock: SharedValue<number>;
  intensity: number;
}) {
  const animatedStyle = useAnimatedStyle(() => {
    // The tap-stir, unchanged in feel: the same seeded keyframe path the jar
    // has always used, now read from a shared value instead of an
    // Animated.Value so it can be summed with the gesture offsets below rather
    // than fighting them for the same transform.
    const frames = [0, 0.18, 0.4, 0.64, 0.84, 1];
    const stirX = interpolate(stir.get(), frames, [
      0,
      motion.firstX * intensity,
      motion.secondX * intensity,
      motion.thirdX * intensity,
      motion.settleX * intensity,
      0,
    ]);
    const stirY = interpolate(stir.get(), frames, [
      0,
      motion.firstY * intensity,
      motion.secondY * intensity,
      motion.thirdY * intensity,
      motion.settleY * intensity,
      0,
    ]);
    const scale = interpolate(
      stir.get(),
      [0, 0.18, 0.42, 0.72, 1],
      [
        1,
        1 + (motion.scaleUp - 1) * intensity,
        1 - 0.03 * intensity,
        1 + 0.035 * intensity,
        1,
      ],
    );

    const offset = composeTokenOffset(
      constants,
      {
        tiltX: tiltX.get(),
        tiltY: tiltY.get(),
        gather: gather.get(),
        scatter: scatter.get(),
        storm: storm.get(),
        stormClock: stormClock.get(),
      },
      intensity,
    );

    return {
      transform: [
        { translateX: stirX + offset.x },
        { translateY: stirY + offset.y },
        { scale },
        { rotate: layout.rotate },
      ],
    };
  });

  return (
    <Animated.View
      style={[
        styles.jarToken,
        {
          width: layout.size,
          height: layout.size,
          left: layout.left,
          bottom: layout.bottom,
          opacity: layout.opacity,
          zIndex: layout.zIndex,
          backgroundColor: token.color,
        },
        animatedStyle,
      ]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <View style={styles.jarTokenRim} />
      <View style={styles.jarTokenHighlight} />
      <View style={[styles.jarTokenAccent, { backgroundColor: token.accent }]} />
      <Text style={styles.jarTokenEmoji}>{token.emoji}</Text>
    </Animated.View>
  );
}

function MoodJarHero({
  tokens,
  entryCount,
  summaryLabel,
  burstSeed,
  styles,
  colors,
}: {
  tokens: MoodJarVisualToken[];
  entryCount: number;
  summaryLabel: string;
  burstSeed: number;
  styles: ReturnType<typeof createStyles>;
  colors: AppColors;
}) {
  const { t } = useTranslation();
  const [reduceMotion, setReduceMotion] = useState(false);
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const [focused, setFocused] = useState(false);

  const stir = useSharedValue(0);
  const tiltX = useSharedValue(0);
  const tiltY = useSharedValue(0);
  const gather = useSharedValue(0);
  const scatter = useSharedValue(0);
  const storm = useSharedValue(0);
  const stormClock = useSharedValue(0);

  /** The sensors' own answer, reported up by `JarSensors`. Never guessed. */
  const [motionAvailable, setMotionAvailable] = useState(false);

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion).catch(() => {});
    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      setReduceMotion,
    );
    return () => subscription.remove();
  }, []);

  useFocusEffect(
    useCallback(() => {
      setFocused(true);
      return () => {
        setFocused(false);
        // Leave the jar level rather than frozen mid-lean: coming back to a
        // tilted jar that no longer answers the phone looks broken.
        tiltX.set(withTiming(0, { duration: JAR_TIMING.tiltLevel }));
        tiltY.set(withTiming(0, { duration: JAR_TIMING.tiltLevel }));
      };
    }, [tiltX, tiltY]),
  );

  /**
   * How much of each effect actually plays. One damper for every gesture, so
   * Reduce Motion cannot be honoured by three of them and forgotten by a
   * fourth. Not zero: the jar still acknowledges a tap, it just stops swinging.
   */
  const intensity = reduceMotion ? 0.12 : 1;

  /**
   * Sensors cost battery, so they are mounted only while they could do
   * something: the screen is a TAB and stays mounted in the background, so
   * "off" has to be reachable or the accelerometer runs for as long as the app
   * is open. Reduce Motion switches them off entirely rather than damping them
   * — there is no point sampling gravity to multiply it by nothing.
   */
  const sensorsMounted = focused && !reduceMotion;
  /** Whether to SAY the jar answers tilt and shake. */
  const motionGesturesLive = sensorsMounted && motionAvailable;

  const runStir = useCallback(() => {
    cancelAnimation(stir);
    stir.set(0);
    stir.set(withTiming(1, {
      duration: reduceMotion ? JAR_TIMING.stirReduced : JAR_TIMING.stir,
      easing: Easing.out(Easing.cubic),
    }));
  }, [stir, reduceMotion]);

  const play = useCallback(() => {
    setShuffleSeed((seed) => seed + 1);
    runStir();
  }, [runStir]);

  useEffect(() => {
    if (burstSeed > 0) runStir();
  }, [burstSeed, runStir]);

  /**
   * Press-in starts a gather; the release decides whether that was a hold.
   *
   * The delay before the gather begins is what keeps a tap feeling like a tap.
   * Without it every tap would start pulling the bubbles inward before the
   * finger lifted, and the stir would begin from a jar that had already moved.
   *
   * ## The stir fires from `onPress`, not from `onPressOut`
   *
   * `onPress` is the canonical "this control was activated" event, and it is
   * the ONLY one a keyboard produces: Enter and Space on a control with
   * `accessibilityRole="button"` fire `onPress` alone, with no press-in and no
   * press-out. An earlier version of this screen drove the stir from
   * `onPressOut` and left keyboard users with a jar that did nothing at all,
   * while the `accessibilityHint` went on promising it would.
   *
   * It also fixes the mouse. Reported on web: a single click did nothing and
   * only a double click stirred. `pressedAt` started at 0, so a press-out that
   * arrived without a matching press-in measured the time since 1970 — read as
   * an extremely long hold, which scatters instead of stirring. The second
   * click then had a real timestamp and worked. The sentinel is `null` now, and
   * "no press-in was seen" is treated as a tap rather than a 54-year hold.
   */
  const pressedAt = useRef<number | null>(null);
  /** Set when a release was a HOLD, so the `onPress` that follows is not also a stir. */
  const releasedAsHold = useRef(false);

  const onPressIn = useCallback(() => {
    pressedAt.current = Date.now();
    releasedAsHold.current = false;
    cancelAnimation(gather);
    gather.set(withDelay(
      HOLD_THRESHOLD_MS,
      withTiming(1, { duration: JAR_TIMING.gather, easing: Easing.out(Easing.quad) }),
    ));
  }, [gather]);

  const onPressOut = useCallback(() => {
    const startedAt = pressedAt.current;
    pressedAt.current = null;
    cancelAnimation(gather);

    // A press we never saw begin is a tap, never a hold. Anything else lets a
    // missing press-in masquerade as an enormous one.
    const held = startedAt === null ? 0 : Date.now() - startedAt;

    if (held < HOLD_THRESHOLD_MS) {
      // Unwind the barely-started gather and leave the stir to `onPress`,
      // which also covers the keyboard.
      gather.set(withTiming(0, { duration: JAR_TIMING.gatherCancel }));
      return;
    }

    // A hold. Let go and the crowd springs apart — the gather unwinds and a
    // brief outward burst rides on top of it.
    releasedAsHold.current = true;
    gather.set(
      withTiming(0, {
        duration: JAR_TIMING.gatherRelease,
        easing: Easing.out(Easing.cubic),
      }),
    );
    cancelAnimation(scatter);
    scatter.set(withSequence(
      withTiming(1, { duration: JAR_TIMING.scatterOut, easing: Easing.out(Easing.quad) }),
      withTiming(0, {
        duration: JAR_TIMING.scatterSettle,
        easing: Easing.out(Easing.cubic),
      }),
    ));
    setShuffleSeed((seed) => seed + 1);
  }, [gather, scatter]);

  const onPress = useCallback(() => {
    // A hold already had its release. Consume the flag so the next activation
    // stirs normally.
    if (releasedAsHold.current) {
      releasedAsHold.current = false;
      return;
    }
    play();
  }, [play]);

  const glowStyle = useAnimatedStyle(() => ({
    opacity:
      interpolate(stir.get(), [0, 0.28, 0.62, 1], [0.18, 0.5, 0.3, 0.18]) +
      storm.get() * 0.25 +
      gather.get() * 0.12,
  }));

  return (
    <View style={styles.jarHero}>
      {sensorsMounted ? (
        <JarSensors
          tiltX={tiltX}
          tiltY={tiltY}
          storm={storm}
          stormClock={stormClock}
          onAvailability={setMotionAvailable}
        />
      ) : null}

      <Pressable
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        // Restored with `onPress`: hovering stirred the jar on desktop before
        // the gesture rewrite dropped it, and losing it was never intended.
        onHoverIn={play}
        accessibilityRole="button"
        accessibilityLabel={t("profile.moodJar.jar.a11yLabel", { count: entryCount })}
        accessibilityHint={t("profile.moodJar.jar.a11yHint")}
        style={styles.jarTapTarget}
      >
        <Animated.View style={[styles.jarGlow, glowStyle]} />
        <View style={styles.jarVisual}>
          <View style={styles.jarLidTop} />
          <View style={styles.jarLid} />
          <View style={styles.jarNeck} />
          <View style={styles.jarBody}>
            <View style={styles.jarBackRim} pointerEvents="none" />
            <View style={styles.jarTokenLayer} pointerEvents="none">
              {tokens.map((token, index) => {
                const layout = tokenLayout(
                  index,
                  tokens.length,
                  token.intensity,
                  token.rotation,
                  shuffleSeed,
                  token.id,
                );
                return (
                  <JarToken
                    key={token.id}
                    token={token}
                    layout={layout}
                    motion={tokenBubbleMotion(
                      index,
                      tokens.length,
                      token.id,
                      layout,
                      shuffleSeed,
                    )}
                    constants={tokenGestureConstants(token.id, layout)}
                    styles={styles}
                    stir={stir}
                    tiltX={tiltX}
                    tiltY={tiltY}
                    gather={gather}
                    scatter={scatter}
                    storm={storm}
                    stormClock={stormClock}
                    intensity={intensity}
                  />
                );
              })}
            </View>
            <View style={styles.jarShine} pointerEvents="none" />
            <View style={styles.jarSideShine} pointerEvents="none" />
            <View style={styles.jarInnerBase} pointerEvents="none" />
          </View>
          <View style={styles.jarBase} />
        </View>
      </Pressable>
      <Text style={styles.jarSummary}>{summaryLabel}</Text>
      <Text style={styles.jarMicrocopy}>
        {/* An empty jar is the normal state at the start of a month — last
            month's moods are cleared once its card is made. Say what filling it
            leads to, rather than leaving the emptiness unexplained.

            The gesture line names only what this device can actually do. Tilt
            and shake need sensors a browser does not have, and promising them
            there would be the Stay Visible bug again. */}
        {tokens.length === 0
          ? t("profile.moodJar.jar.emptyHint")
          : motionGesturesLive
            ? t("profile.moodJar.jar.microcopyMotion")
            : t("profile.moodJar.jar.microcopy")}
      </Text>
    </View>
  );
}

/**
 * The per-bubble constants the gesture worklets need, derived from where the
 * bubble already sits.
 *
 * Computed in JS and passed down as plain numbers, because a worklet may
 * capture numbers freely but may not capture the seeded-RNG closure that
 * produced them.
 */
function tokenGestureConstants(
  id: string,
  layout: TokenLayout,
): TokenMotionConstants {
  const centreLeft = (JAR_BODY_WIDTH - layout.size) / 2;
  const centreBottom = (JAR_BODY_HEIGHT - layout.size) / 2;

  // The TRUE room, per direction, in translate space. `bottom` grows upward
  // while `translateY` grows downward, so the vertical pair is crossed over.
  const roomLeft = Math.max(0, layout.left - JAR_TOKEN_INSET);
  const roomRight = Math.max(
    0,
    JAR_BODY_WIDTH - JAR_TOKEN_INSET - layout.size - layout.left,
  );
  const roomDown = Math.max(0, layout.bottom - JAR_TOKEN_INSET);
  const roomUp = Math.max(
    0,
    JAR_BODY_HEIGHT - JAR_TOKEN_INSET - layout.size - layout.bottom,
  );

  // Symmetric span, for how far a TILT or a storm orbit throws the bubble.
  // Kept even-handed so a lean feels the same wherever a bubble sits; it is a
  // magnitude, never a limit.
  const driftSpanX = Math.min(roomLeft, roomRight);
  const driftSpanY = Math.min(roomDown, roomUp);

  /**
   * The gather travels the WHOLE way to the middle, not most of it.
   *
   * This was `* 0.72`, which left every bubble a fifth of its journey short and
   * the crowd visibly hollow. The clamp above was the larger half of the same
   * bug: a symmetric bound meant an edge bubble could not cross the jar at all.
   *
   * A small seeded offset keeps them from stacking into one perfect disc — the
   * point is a tight clump, and thirty-five circles sharing an exact centre
   * reads as a single bubble rather than a crowd.
   */
  const clusterX = seededRange(`${id}:cluster-x`, -1, 1) * layout.size * 0.42;
  const clusterY = seededRange(`${id}:cluster-y`, -1, 1) * layout.size * 0.42;
  const gatherX = centreLeft - layout.left + clusterX;
  const gatherY = -(centreBottom - layout.bottom) + clusterY;

  const spread = Math.hypot(gatherX, gatherY) || 1;
  const scatterReach = seededRange(`${id}:scatter`, 0.5, 1.15);

  return {
    // Bigger bubbles lag: a heavy marble answers a lean less than a small one,
    // which is what stops the crowd sliding as one rigid block.
    driftBias: clamp(
      1.18 - layout.size / 46 + seededRange(`${id}:bias`, -0.12, 0.12),
      0.32,
      1.2,
    ),
    driftSpanX,
    driftSpanY,
    gatherX,
    gatherY,
    scatterX: (-gatherX / spread) * driftSpanX * scatterReach,
    scatterY: (-gatherY / spread) * driftSpanY * scatterReach,
    stormPhase: seededRange(`${id}:phase`, 0, Math.PI * 2),
    stormRadius:
      Math.max(6, Math.min(driftSpanX, driftSpanY)) *
      seededRange(`${id}:radius`, 0.5, 1),
    minX: -roomLeft,
    maxX: roomRight,
    minY: -roomUp,
    maxY: roomDown,
  };
}

function MoodOptionSection({
  title,
  badge,
  options,
  selectedId,
  labelFor,
  onSelect,
  styles,
  colors,
  optionKindKey,
  compact = false,
  otherValue,
  onOtherChange,
  otherPlaceholder,
  otherCharacterCount,
  showOtherInput = false,
  collapsible = false,
  expanded = true,
  collapsedRows = 2,
  toggleExpanded,
  showMoreLabel,
  showLessLabel,
}: {
  title: string;
  badge?: string;
  options: MoodCatalogItem[];
  selectedId: string | null;
  labelFor: (id: string) => string;
  onSelect: (id: string) => void;
  styles: ReturnType<typeof createStyles>;
  colors: AppColors;
  optionKindKey: TranslationKey;
  compact?: boolean;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  otherPlaceholder?: string;
  otherCharacterCount?: string;
  showOtherInput?: boolean;
  collapsible?: boolean;
  expanded?: boolean;
  collapsedRows?: number;
  toggleExpanded?: () => void;
  showMoreLabel?: string;
  showLessLabel?: string;
}) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const contentWidth = Math.min(Math.max(width - 52, 260), 760);
  const estimatedColumns = Math.max(2, Math.floor((contentWidth + 7) / 99));
  const visibleCount = estimatedColumns * collapsedRows;
  const shouldCollapse = collapsible && options.length > visibleCount;
  const visibleOptions = shouldCollapse && !expanded ? options.slice(0, visibleCount) : options;

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text accessibilityRole="header" style={styles.sectionTitle}>
          {title}
        </Text>
        {badge ? <Text style={styles.sectionBadge}>{badge}</Text> : null}
      </View>
      <View style={styles.grid}>
        {visibleOptions.map((m) => {
          const active = m.id === selectedId;
          const label = labelFor(m.id);
          return (
            <Pressable
              key={m.id}
              onPress={() => onSelect(m.id)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              // No ", selected" suffix: `accessibilityState` above already carries
              // it, and both VoiceOver and TalkBack announce that themselves —
              // spelling it out in the label had every chip read its state twice.
              accessibilityLabel={t(optionKindKey, { label })}
              style={[styles.moodChip, compact && styles.moodChipCompact, active && styles.moodChipActive]}
            >
              <Text style={[styles.moodEmoji, compact && styles.moodEmojiCompact]}>{m.emoji}</Text>
              <Text style={[styles.moodLabel, active && styles.moodLabelActive]} numberOfLines={2}>
                {label}
              </Text>
              {active ? (
                <View style={styles.check}>
                  <Ionicons name="checkmark" size={12} color={colors.primaryText} />
                </View>
              ) : null}
            </Pressable>
          );
        })}
      </View>
      {shouldCollapse && toggleExpanded ? (
        <Pressable
          onPress={toggleExpanded}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          style={styles.moodToggle}
        >
          <Text style={styles.moodToggleText}>
            {expanded ? showLessLabel : showMoreLabel}
          </Text>
          <Ionicons
            name={expanded ? "chevron-up" : "chevron-down"}
            size={15}
            color={colors.primaryText}
          />
        </Pressable>
      ) : null}
      {showOtherInput && onOtherChange && otherPlaceholder ? (
        <View style={styles.otherWrap}>
          <TextInput
            value={otherValue ?? ""}
            onChangeText={onOtherChange}
            placeholder={otherPlaceholder}
            placeholderTextColor={colors.textMuted}
            maxLength={MOOD_OTHER_MAX}
            style={styles.otherInput}
            accessibilityLabel={otherPlaceholder}
          />
          {otherCharacterCount ? (
            <Text style={styles.counterText}>{otherCharacterCount}</Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function MoodContextSection({
  title,
  badge,
  options,
  selectedId,
  labelFor,
  onSelect,
  contextOther,
  onContextOtherChange,
  styles,
  colors,
  placeholder,
  characterCount,
}: {
  title: string;
  badge: string;
  options: MoodContextCatalogItem[];
  selectedId: string | null;
  labelFor: (id: string) => string;
  onSelect: (id: string) => void;
  contextOther: string;
  onContextOtherChange: (value: string) => void;
  styles: ReturnType<typeof createStyles>;
  colors: AppColors;
  placeholder: string;
  characterCount: string;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text accessibilityRole="header" style={styles.sectionTitle}>
          {title}
        </Text>
        <Text style={styles.sectionBadge}>{badge}</Text>
      </View>
      <View style={styles.contextGrid}>
        {options.map((m) => {
          const active = m.id === selectedId;
          const label = labelFor(m.id);
          return (
            <Pressable
              key={m.id}
              onPress={() => onSelect(m.id)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={t("profile.moodJar.a11yContextOption", { label })}
              style={[styles.contextChip, active && styles.moodChipActive]}
            >
              <Text style={styles.contextEmoji}>{m.emoji}</Text>
              <Text style={[styles.contextLabel, active && styles.moodLabelActive]} numberOfLines={1}>
                {label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      {selectedId === "other" ? (
        <View style={styles.otherWrap}>
          <TextInput
            value={contextOther}
            onChangeText={onContextOtherChange}
            placeholder={placeholder}
            placeholderTextColor={colors.textMuted}
            maxLength={CONTEXT_OTHER_MAX}
            style={styles.otherInput}
            accessibilityLabel={placeholder}
          />
          <Text style={styles.counterText}>{characterCount}</Text>
        </View>
      ) : null}
    </View>
  );
}

function buildVisualTokens(
  entries: MoodJarEntry[],
  moodOptions: MoodCatalogItem[],
): MoodJarVisualToken[] {
  const emojiByMood = new Map(moodOptions.map((m) => [m.id, m.emoji]));
  return entries.slice(-VISIBLE_TOKEN_LIMIT).map((entry) => ({
    id: entry.id,
    moodId: entry.moodId,
    emoji: emojiByMood.get(entry.moodId) ?? (entry.moodId === "other" ? "✎" : "•"),
    intensity: entry.intensity,
    ...moodMarbleColors(entry.moodId),
    rotation: seededRange(entry.id, -8, 8),
  }));
}

function tokenLayout(
  index: number,
  total: number,
  intensity: number,
  rotation: number,
  shuffleSeed: number,
  id: string,
): TokenLayout {
  const size = marbleSize(total, intensity);
  const shuffledIndex = total > 0 ? (index + shuffleSeed) % total : index;
  const shuffleRotation = rotation + seededRange(`${id}:${shuffleSeed}:rotate`, -5, 5);

  if (total <= 8) {
    const slots = settledSlots(total);
    const slot = slots[shuffledIndex] ?? slots[slots.length - 1];
    const shuffleX = seededRange(`${id}:${shuffleSeed}:slot-x`, -5, 5);
    const shuffleY = seededRange(`${id}:${shuffleSeed}:slot-y`, -3, 3);
    return {
      size,
      left: clamp(
        slot.left - size / 2 + shuffleX,
        JAR_TOKEN_INSET,
        JAR_BODY_WIDTH - JAR_TOKEN_INSET - size,
      ),
      bottom: clamp(
        slot.bottom + shuffleY,
        JAR_TOKEN_INSET,
        JAR_BODY_HEIGHT - JAR_TOKEN_INSET - size,
      ),
      opacity: 1,
      zIndex: shuffledIndex,
      rotate: `${shuffleRotation}deg`,
    };
  }

  const dense = total > 28;
  const columns = dense ? 5 : 4;
  const spacingX = dense ? 39 : 45;
  const spacingY = dense ? 34 : 40;
  const row = Math.floor(shuffledIndex / columns);
  const col = shuffledIndex % columns;
  const rowOffset = dense
    ? row % 2 === 0
      ? -spacingX * 0.12
      : spacingX * 0.16
    : row % 2 === 0
      ? 0
      : spacingX / 2;
  const jarInnerWidth = 236;
  const rowStart = dense
    ? (jarInnerWidth - size - (columns - 1) * spacingX) / 2
    : Math.max(0, 14 - row * 2) + 18;
  const wobbleX = seededRange(`${id}:${shuffleSeed}:x`, -6, 6);
  const wobbleY = seededRange(`${id}:${shuffleSeed}:y`, -5, 5);
  const maxRow = Math.max(0, Math.floor((Math.min(total, VISIBLE_TOKEN_LIMIT) - 1) / columns));

  return {
    size,
    left: clamp(
      rowStart + col * spacingX + rowOffset + wobbleX,
      JAR_TOKEN_INSET,
      JAR_BODY_WIDTH - JAR_TOKEN_INSET - size,
    ),
    bottom: clamp(
      10 + row * spacingY + wobbleY,
      JAR_TOKEN_INSET,
      JAR_BODY_HEIGHT - JAR_TOKEN_INSET - size,
    ),
    opacity: row < maxRow - 3 ? 0.74 : 1,
    zIndex: row * columns + col,
    rotate: `${shuffleRotation}deg`,
  };
}

function tokenBubbleMotion(
  index: number,
  total: number,
  id: string,
  layout: TokenLayout,
  shuffleSeed: number,
): {
  firstX: number;
  firstY: number;
  secondX: number;
  secondY: number;
  thirdX: number;
  thirdY: number;
  settleX: number;
  settleY: number;
  scaleUp: number;
} {
  const minLeft = JAR_TOKEN_INSET;
  const maxLeft = Math.max(
    minLeft,
    JAR_BODY_WIDTH - JAR_TOKEN_INSET - layout.size,
  );
  const minBottom = JAR_TOKEN_INSET;
  const maxBottom = Math.max(
    minBottom,
    JAR_BODY_HEIGHT - JAR_TOKEN_INSET - layout.size,
  );
  const horizontalSpan = maxLeft - minLeft;
  const verticalSpan = maxBottom - minBottom;
  const currentLeft = clamp(layout.left, minLeft, maxLeft);
  const currentBottom = clamp(layout.bottom, minBottom, maxBottom);
  const stirKey = `${id}:stir:${shuffleSeed}`;
  const direction = seededRange(`${stirKey}:direction`, 0, 1) >= 0.5 ? 1 : -1;
  const sideSeed = Math.floor(seededRange(`${stirKey}:side`, 0, 4));
  const sideAt = (step: number) =>
    ((sideSeed + index + direction * step) % 4 + 4) % 4;
  const point = (name: string, side: number, insetScale: number) => {
    const edgeProgress = seededRange(`${stirKey}:${name}:edge`, 0.08, 0.92);
    const edgeInset =
      seededRange(`${stirKey}:${name}:inset`, 0.02, 0.18) * insetScale;
    const jitterX = seededRange(`${stirKey}:${name}:jitter-x`, -5, 5);
    const jitterY = seededRange(`${stirKey}:${name}:jitter-y`, -5, 5);

    if (side === 0) {
      return {
        left: clamp(
          minLeft + horizontalSpan * edgeProgress + jitterX,
          minLeft,
          maxLeft,
        ),
        bottom: clamp(
          minBottom + verticalSpan * edgeInset + jitterY,
          minBottom,
          maxBottom,
        ),
      };
    }
    if (side === 1) {
      return {
        left: clamp(
          maxLeft - horizontalSpan * edgeInset + jitterX,
          minLeft,
          maxLeft,
        ),
        bottom: clamp(
          minBottom + verticalSpan * edgeProgress + jitterY,
          minBottom,
          maxBottom,
        ),
      };
    }
    if (side === 2) {
      return {
        left: clamp(
          maxLeft - horizontalSpan * edgeProgress + jitterX,
          minLeft,
          maxLeft,
        ),
        bottom: clamp(
          maxBottom - verticalSpan * edgeInset + jitterY,
          minBottom,
          maxBottom,
        ),
      };
    }
    return {
      left: clamp(
        minLeft + horizontalSpan * edgeInset + jitterX,
        minLeft,
        maxLeft,
      ),
      bottom: clamp(
        maxBottom - verticalSpan * edgeProgress + jitterY,
        minBottom,
        maxBottom,
      ),
    };
  };
  const first = point(
    "first",
    sideAt(0),
    seededRange(`${stirKey}:scale-1`, 0.78, 1),
  );
  const second = point(
    "second",
    sideAt(1),
    seededRange(`${stirKey}:scale-2`, 0.72, 1),
  );
  const third = point(
    "third",
    sideAt(2),
    seededRange(`${stirKey}:scale-3`, 0.78, 1),
  );
  const settleLeft = clamp(
    currentLeft + seededRange(`${stirKey}:settle-x`, -8, 8),
    minLeft,
    maxLeft,
  );
  const settleBottom = clamp(
    currentBottom + seededRange(`${stirKey}:settle-y`, -5, 5),
    minBottom,
    maxBottom,
  );

  return {
    firstX: first.left - currentLeft,
    firstY: currentBottom - first.bottom,
    secondX: second.left - currentLeft,
    secondY: currentBottom - second.bottom,
    thirdX: third.left - currentLeft,
    thirdY: currentBottom - third.bottom,
    settleX: settleLeft - currentLeft,
    settleY: currentBottom - settleBottom,
    scaleUp: total > 20 ? 1.025 : 1.045 + (index % 3) * 0.012,
  };
}

function marbleSize(total: number, intensity: number): number {
  const cappedIntensity = Math.min(5, Math.max(1, Math.round(intensity)));
  // Reduced roughly 18% across every level. The old sizes filled the jar so
  // completely that a gather had nowhere to gather TO — the crowd could only
  // ever be as tight as the marbles were big.
  const base = total <= 3 ? 48 : total <= 8 ? 43 : total <= 16 ? 38 : total <= 28 ? 33 : 28;
  return Math.min(54, Math.max(28, base + (cappedIntensity - 3) * 3));
}

function settledSlots(total: number): { left: number; bottom: number }[] {
  if (total === 0) return [];
  if (total === 1) return [{ left: 112, bottom: 16 }];
  if (total === 2) return [{ left: 88, bottom: 15 }, { left: 136, bottom: 17 }];
  if (total === 3) {
    return [
      { left: 76, bottom: 14 },
      { left: 127, bottom: 15 },
      { left: 103, bottom: 58 },
    ];
  }
  if (total <= 5) {
    return [
      { left: 66, bottom: 14 },
      { left: 116, bottom: 13 },
      { left: 164, bottom: 16 },
      { left: 91, bottom: 58 },
      { left: 140, bottom: 61 },
    ];
  }
  return [
    { left: 60, bottom: 14 },
    { left: 112, bottom: 12 },
    { left: 164, bottom: 15 },
    { left: 84, bottom: 58 },
    { left: 138, bottom: 60 },
    { left: 112, bottom: 104 },
    { left: 62, bottom: 99 },
    { left: 166, bottom: 101 },
  ];
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function moodMarbleColors(moodId: string): { color: string; accent: string } {
  const colors: Record<string, { color: string; accent: string }> = {
    happy: { color: "#F5B73F", accent: "#FFE3A1" },
    delighted: { color: "#F8C75B", accent: "#FFF1BA" },
    excited: { color: "#F0A33A", accent: "#FFE0A0" },
    proud: { color: "#E7B64A", accent: "#FFF0A7" },
    energized: { color: "#2E8CFF", accent: "#9DD4FF" },
    calm: { color: "#49B8C2", accent: "#B6EDF1" },
    centered: { color: "#73B9C7", accent: "#D4F2F6" },
    content: { color: "#75C6A5", accent: "#D4F5E8" },
    peaceful: { color: "#88C8B2", accent: "#E1F7EE" },
    relaxed: { color: "#7DC5A0", accent: "#D5F6E4" },
    present: { color: "#80B8D8", accent: "#D6EEFF" },
    sad: { color: "#3478C7", accent: "#A8D4FF" },
    grieving: { color: "#263E65", accent: "#93AFD8" },
    nervous: { color: "#77B9EC", accent: "#D7EEFF" },
    worried: { color: "#6E95D8", accent: "#C5DAFF" },
    anxious: { color: "#6FA8D9", accent: "#D7ECFF" },
    overthinking: { color: "#7A61D2", accent: "#D8CBFF" },
    overwhelmed: { color: "#4E75B8", accent: "#B7CCF4" },
    stressed: { color: "#5986C7", accent: "#C3D8FB" },
    burned_out: { color: "#3F526E", accent: "#AEBED4" },
    meh: { color: "#7891A9", accent: "#D0DCE7" },
    other: { color: "#6EA8FF", accent: "#D9E9FF" },
  };
  return colors[moodId] ?? { color: "#6EA8FF", accent: "#D9E9FF" };
}

function seededRange(seed: string, min: number, max: number): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 9973;
  }
  return min + (hash / 9973) * (max - min);
}

function currentMonthPeriodKey(): string {
  return new Date().toISOString().slice(0, 7);
}

function createStyles(c: AppColors) {
  return StyleSheet.create({
    safe: { flex: 1, backgroundColor: c.background },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    headerBtn: { width: 40, height: 40, alignItems: "center", justifyContent: "center" },
    headerTitle: { fontSize: 17, fontWeight: "700", color: c.text },
    body: { padding: 16, paddingBottom: 40 },
    form: { width: "100%", maxWidth: 760, alignSelf: "center", gap: 16 },
    jarHero: {
      alignItems: "center",
      gap: 8,
      paddingTop: 14,
      paddingBottom: 16,
      paddingHorizontal: 14,
      borderRadius: 22,
      borderWidth: 1,
      borderColor: "#2C4666",
      backgroundColor: "#13283D",
      overflow: "hidden",
    },
    jarTapTarget: {
      width: 292,
      maxWidth: "100%",
      minHeight: 316,
      alignItems: "center",
      justifyContent: "center",
    },
    jarGlow: {
      position: "absolute",
      width: 260,
      height: 104,
      borderRadius: 138,
      backgroundColor: "rgba(83,154,255,0.34)",
      bottom: 14,
    },
    jarVisual: {
      width: 270,
      height: 300,
      alignItems: "center",
      justifyContent: "flex-start",
    },
    jarLidTop: {
      width: 158,
      height: 28,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: "#8AB8FF",
      backgroundColor: "#2F74F5",
      shadowColor: "#7FB6FF",
      shadowOpacity: 0.32,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 0 },
      zIndex: 7,
    },
    jarLid: {
      width: 170,
      height: 34,
      marginTop: -9,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: "#174BAF",
      borderBottomColor: "#0E347E",
      backgroundColor: "#185AD7",
      zIndex: 6,
    },
    jarNeck: {
      width: 126,
      height: 24,
      marginTop: -1,
      borderTopWidth: 0,
      borderWidth: 2,
      borderColor: "rgba(178,214,255,0.72)",
      backgroundColor: "rgba(82,138,191,0.20)",
      zIndex: 4,
    },
    jarBody: {
      width: JAR_BODY_WIDTH,
      height: JAR_BODY_HEIGHT,
      marginTop: -1,
      borderRadius: 50,
      borderTopLeftRadius: 36,
      borderTopRightRadius: 36,
      borderWidth: 3,
      borderColor: "rgba(190,226,255,0.78)",
      backgroundColor: "rgba(40,86,126,0.28)",
      overflow: "hidden",
      shadowColor: "#72B6FF",
      shadowOpacity: 0.26,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: 0 },
    },
    jarTokenLayer: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    },
    jarBackRim: {
      position: "absolute",
      top: 12,
      left: 42,
      right: 42,
      height: 18,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: "rgba(178,214,255,0.38)",
      backgroundColor: "rgba(5,20,36,0.14)",
    },
    jarToken: {
      position: "absolute",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 999,
      borderWidth: 0,
      shadowColor: "#000",
      shadowOpacity: 0.28,
      shadowRadius: 9,
      shadowOffset: { width: 0, height: 6 },
      elevation: 3,
      overflow: "hidden",
    },
    jarTokenRim: {
      position: "absolute",
      left: 1,
      right: 1,
      top: 1,
      bottom: 1,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: "rgba(255,255,255,0.44)",
    },
    jarTokenHighlight: {
      position: "absolute",
      top: 7,
      left: 9,
      width: 16,
      height: 10,
      borderRadius: 999,
      backgroundColor: "rgba(255,255,255,0.66)",
    },
    jarTokenAccent: {
      position: "absolute",
      right: -10,
      bottom: -10,
      width: 34,
      height: 34,
      borderRadius: 17,
      opacity: 0.24,
    },
    jarTokenEmoji: { fontSize: 21, lineHeight: 24, zIndex: 2 },
    jarShine: {
      position: "absolute",
      top: 24,
      left: 23,
      width: 28,
      height: 150,
      borderRadius: 999,
      backgroundColor: "rgba(255,255,255,0.24)",
      opacity: 0.86,
    },
    jarSideShine: {
      position: "absolute",
      top: 64,
      right: 25,
      width: 12,
      height: 78,
      borderRadius: 999,
      backgroundColor: "rgba(180,223,255,0.18)",
    },
    jarInnerBase: {
      position: "absolute",
      left: 30,
      right: 30,
      bottom: 12,
      height: 20,
      borderRadius: 999,
      backgroundColor: "rgba(181,222,255,0.22)",
    },
    jarBase: {
      width: 200,
      height: 20,
      marginTop: -12,
      borderRadius: 999,
      backgroundColor: "rgba(102,171,255,0.28)",
    },
    jarSummary: { fontSize: 15, fontWeight: "800", color: "#EFF5FF", textAlign: "center" },
    jarMicrocopy: {
      maxWidth: 300,
      fontSize: 13,
      color: "#A8B6C9",
      lineHeight: 18,
      textAlign: "center",
    },
    section: {
      gap: 8,
      padding: 10,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surface,
    },
    sectionHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10 },
    sectionTitle: { flex: 1, fontSize: 14, fontWeight: "800", color: c.text },
    sectionBadge: {
      fontSize: 11,
      fontWeight: "700",
      color: c.textSecondary,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: c.surfaceMuted,
      overflow: "hidden",
    },
    grid: { flexDirection: "row", flexWrap: "wrap", gap: 7 },
    contextGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
    moodChip: {
      position: "relative",
      flexGrow: 1,
      flexBasis: 92,
      maxWidth: 128,
      minHeight: 56,
      gap: 3,
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 7,
      paddingHorizontal: 7,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surfaceMuted,
    },
    moodChipCompact: {
      flexBasis: 100,
      minHeight: 52,
      maxWidth: 132,
    },
    moodChipActive: { borderColor: c.primary, backgroundColor: c.primarySurface },
    moodEmoji: { fontSize: 19, lineHeight: 22 },
    moodEmojiCompact: { fontSize: 17, lineHeight: 20 },
    moodLabel: { fontSize: 10.5, fontWeight: "700", color: c.textBody, textAlign: "center" },
    moodLabelActive: { color: c.primaryText },
    moodToggle: {
      alignSelf: "center",
      minHeight: 32,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      paddingHorizontal: 12,
      borderRadius: 999,
      backgroundColor: c.primarySurface,
    },
    moodToggleText: { fontSize: 11.5, fontWeight: "800", color: c.primaryText },
    contextChip: {
      minHeight: 34,
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      paddingVertical: 6,
      paddingHorizontal: 8,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surfaceMuted,
    },
    contextEmoji: { fontSize: 14, lineHeight: 17 },
    contextLabel: { fontSize: 11.5, fontWeight: "700", color: c.textBody },
    otherWrap: {
      gap: 6,
    },
    otherInput: {
      minHeight: 44,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surfaceMuted,
      paddingHorizontal: 12,
      color: c.text,
      fontSize: 14,
    },
    counterText: { alignSelf: "flex-end", fontSize: 12, color: c.textMuted },
    check: {
      position: "absolute",
      top: 5,
      right: 5,
      width: 16,
      height: 16,
      borderRadius: 8,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: c.primary,
    },
    intensityRow: { flexDirection: "row", gap: 8, alignItems: "center" },
    intensitySlider: { flex: 1, height: 28 },
    intensityHint: { minWidth: 42, fontSize: 12, color: c.textMuted },
    addBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      minHeight: 52,
      borderRadius: 14,
      backgroundColor: c.primary,
    },
    addText: { fontSize: 16, fontWeight: "800", color: "#fff" },
    btnDisabled: { opacity: 0.55 },
    devBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      minHeight: 40,
      borderRadius: 10,
      borderWidth: 1,
      borderStyle: "dashed",
      borderColor: c.border,
    },
    devBtnText: { fontSize: 13, fontWeight: "600", color: c.textSecondary },
  });
}
